import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

interface HistogramProps {
  data: number[];
  title?: string;
}

export const Histogram: React.FC<HistogramProps> = ({ data, title = 'Distribution des calibres' }) => {
  if (data.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px', 
        color: '#666',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        margin: '20px 0'
      }}>
        Aucune donnée disponible pour l&apos;histogramme
      </div>
    );
  }

  // Create histogram bins with integer min/max and adaptive bin size
  const min = Math.floor(Math.min(...data));
  const max = Math.floor(Math.max(...data)) + 1;
  const range = max - min;
  
  // Determine bin size based on range
  let binSize: number;
  if (range < 1) {
    binSize = 0.1;
  } else if (range < 3) {
    binSize = 0.5;
  } else {
    binSize = 1;
  }
  
  const binCount = Math.ceil(range / binSize);
  
  const bins = Array(binCount).fill(0);
  const binLabels = [];
  
  for (let i = 0; i < binCount; i++) {
    const binStart = min + i * binSize;
    const binEnd = min + (i + 1) * binSize;
    binLabels.push(`${binStart.toFixed(1)}-${binEnd.toFixed(1)}`);
  }
  
  // Count values in each bin
  data.forEach(value => {
    const binIndex = Math.min(Math.floor((value - min) / binSize), binCount - 1);
    bins[binIndex]++;
  });

  const chartData = binLabels.map((label, index) => ({
    range: label,
    count: bins[index],
  }));

  // Calculate Y-axis range for ticks
  const maxCount = Math.max(...bins);
  const yAxisMax = Math.ceil(maxCount * 1.1); // Add 10% padding

  return (
    <div style={{ margin: '20px 0', border: '1px solid #ccc', borderRadius: '8px' }}>
      <h4 style={{ 
        textAlign: 'center', 
        marginBottom: '20px', 
        color: '#333',
        fontSize: '18px',
        fontWeight: '500'
      }}>
        {title}
      </h4>
      <ResponsiveContainer width="100%" height={300} style={{ pointerEvents: 'none' }}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="range" 
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={false}
            tickLine={false}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis 
            domain={[0, yAxisMax]}
            tick={{ fontSize: 12, fill: '#666' }}
            axisLine={false}
            tickLine={false}
            ticks={[
              0,
              Math.round(yAxisMax / 3 + 0.5),
              Math.round(2 * yAxisMax / 3 + 0.5),
              yAxisMax
            ]}
            label={{ 
              value: 'Fréquence', 
              angle: -90, 
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666' }
            }}
          />
          <Bar 
            dataKey="count" 
            fill="#6366f1" 
            radius={[4, 4, 0, 0]}
            stroke="#4f46e5"
            strokeWidth={1}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
      <div style={{ 
        textAlign: 'center', 
        marginTop: '10px',
        color: '#666',
        fontSize: '14px'
      }}>
        Calibre (mm)
      </div>
    </div>
  );
}; 